import { useEffect, useRef } from 'react';

import { addStyles } from '../../utils/helpers';

export type ListItem = {
  // unique key attribute (defaults to array index)
  key?: string | number;
  // Content to render as the list item body
  body: React.ReactNode;
  // Show the list item as highlighted in the menu
  active?: boolean;
};

export type MenuListProps = {
  // Collection of list items to render
  listItems: ListItem[];
  // class names
  className?: string;
  // Handle click menu item
  handleClickItem?: (itemIndex: ListItem['key']) => void;
  handleLoadMore?: () => void;
};

const options = {
  listItem: {
    base: 'flex space-x-2 items-center px-4 py-2 cursor-pointer border-r-4 hover:bg-jungle-50 hover:border-r-4 hover:border-primary',
    active: 'bg-jungle-50 text-primary fill-primary border-r-4 border-primary',
    inactive: 'border-white',
  },
};

export const MenuList = (props: MenuListProps) => {
  const { listItems, className = 'w-full', handleClickItem, handleLoadMore } = props;

  const intersectionRef = useRef<HTMLLIElement>(null);

  useEffect(() => {
    if (!handleLoadMore) return;
    if (intersectionRef.current) {
      const element = intersectionRef.current;
      const observer = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          // load more list items
          handleLoadMore();
        }
      });
      observer.observe(element);
      return () => {
        observer.unobserve(element);
      };
    }
    return;
  }, [intersectionRef, handleLoadMore]);

  return (
    <ul className={addStyles(className)}>
      {listItems.map(({ key, body, active }, index) => (
        <li
          key={key ?? index}
          aria-current={active}
          onClick={() => handleClickItem && handleClickItem(key ?? index)}
          className={addStyles([options.listItem.base, active ? options.listItem.active : options.listItem.inactive])}
          ref={index === listItems.length - 1 ? intersectionRef : undefined}
        >
          {body}
        </li>
      ))}
    </ul>
  );
};
export default MenuList;
